/**
 * Browser API utilities with SSR safety
 *
 * Provides safe access to browser-only APIs that would crash during SSR.
 * All functions handle server-side rendering gracefully with fallback values.
 *
 * @example
 * ```typescript
 * // Instead of: navigator.userAgent
 * const ua = BrowserAPI.navigator.userAgent;
 *
 * // Instead of: window.location.href = url
 * BrowserAPI.location.setHref(url);
 * ```
 */

/**
 * Check if code is running in browser environment
 */
export const isBrowser = (): boolean => import.meta.client;

/**
 * Check if code is running on server
 */
export const isServer = (): boolean => import.meta.server;

/**
 * Safe navigator API access
 */
export const SafeNavigator = {
  /**
   * Get user agent string
   * @returns User agent on client, 'server' on SSR
   */
  get userAgent(): string {
    return isBrowser() ? navigator.userAgent : "server";
  },

  /**
   * Get platform string
   * @returns Platform on client, 'server' on SSR
   */
  get platform(): string {
    return isBrowser() ? navigator.platform : "server";
  },

  /**
   * Get language string
   * @returns Language on client, 'en' on SSR
   */
  get language(): string {
    return isBrowser() ? navigator.language : "en";
  },
} as const;

/**
 * Safe window API access
 */
export const SafeWindow = {
  /**
   * Get window object if available
   * @returns Window object on client, null on SSR
   */
  get instance(): Window | null {
    return isBrowser() ? window : null;
  },

  /**
   * Add event listener safely
   */
  addEventListener<K extends keyof WindowEventMap>(
    type: K,
    listener: (this: Window, ev: WindowEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions,
  ): void {
    if (isBrowser()) {
      window.addEventListener(type, listener, options);
    }
  },

  /**
   * Remove event listener safely
   */
  removeEventListener<K extends keyof WindowEventMap>(
    type: K,
    listener: (this: Window, ev: WindowEventMap[K]) => any,
    options?: boolean | EventListenerOptions,
  ): void {
    if (isBrowser()) {
      window.removeEventListener(type, listener, options);
    }
  },
} as const;

/**
 * Safe location API access
 */
export const SafeLocation = {
  /**
   * Get current href
   * @returns Current URL on client, empty string on SSR
   */
  get href(): string {
    return isBrowser() ? window.location.href : "";
  },

  /**
   * Set location href (navigate)
   * @param url - URL to navigate to
   */
  setHref(url: string): void {
    if (isBrowser()) {
      window.location.href = url;
    }
  },

  /**
   * Get pathname
   * @returns Current pathname on client, '/' on SSR
   */
  get pathname(): string {
    return isBrowser() ? window.location.pathname : "/";
  },

  /**
   * Reload page
   */
  reload(): void {
    if (isBrowser()) {
      window.location.reload();
    }
  },
} as const;

/**
 * Safe document API access
 */
export const SafeDocument = {
  /**
   * Get element by ID
   * @param id - Element ID
   * @returns Element on client, null on SSR
   */
  getElementById(id: string): HTMLElement | null {
    return isBrowser() ? document.getElementById(id) : null;
  },

  /**
   * Query selector
   * @param selector - CSS selector
   * @returns Element on client, null on SSR
   */
  querySelector<E extends Element = Element>(selector: string): E | null {
    return isBrowser() ? document.querySelector<E>(selector) : null;
  },

  /**
   * Query selector all
   * @param selector - CSS selector
   * @returns NodeList on client, empty array on SSR
   */
  querySelectorAll<E extends Element = Element>(selector: string): NodeListOf<E> | E[] {
    return isBrowser() ? document.querySelectorAll<E>(selector) : [];
  },

  /**
   * Create element
   * @param tagName - Tag name
   * @returns Element on client, null on SSR
   */
  createElement<K extends keyof HTMLElementTagNameMap>(
    tagName: K,
  ): HTMLElementTagNameMap[K] | null {
    return isBrowser() ? document.createElement(tagName) : null;
  },

  /**
   * Get document head
   * @returns Head element on client, null on SSR
   */
  get head(): HTMLHeadElement | null {
    return isBrowser() ? document.head : null;
  },

  /**
   * Get document body
   * @returns Body element on client, null on SSR
   */
  get body(): HTMLElement | null {
    return isBrowser() ? document.body : null;
  },
} as const;

/**
 * Execute callback only in browser environment
 * @param callback - Function to execute
 * @returns Return value of callback or null
 */
export function clientOnly<T>(callback: () => T): T | null {
  return isBrowser() ? callback() : null;
}

/**
 * Execute callback only on server
 * @param callback - Function to execute
 * @returns Return value of callback or null
 */
export function serverOnly<T>(callback: () => T): T | null {
  return isServer() ? callback() : null;
}

/**
 * Browser API namespace (aggregated export)
 */
export const BrowserAPI = {
  isBrowser,
  isServer,
  navigator: SafeNavigator,
  window: SafeWindow,
  location: SafeLocation,
  document: SafeDocument,
  clientOnly,
  serverOnly,
} as const;
